import { useState } from 'react';

export interface UseToolsSyncStateProps {
  // External state for performance optimization (optional)
  markerSelectionModeByChart?: Record<string, boolean>;
  setMarkerSelectionModeByChart?: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  harmonicSelectionModeByChart?: Record<string, boolean>;
  setHarmonicSelectionModeByChart?: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  sideBandsSelectionModeByChart?: Record<string, boolean>;
  setSideBandsSelectionModeByChart?: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  customLineModeByChart?: Record<string, string | null>;
  setCustomLineModeByChart?: React.Dispatch<React.SetStateAction<Record<string, string | null>>>;
}

export interface UseToolsSyncStateReturn {
  markerSelectionModeByChart: Record<string, boolean>;
  setMarkerSelectionModeByChart: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  harmonicSelectionModeByChart: Record<string, boolean>;
  setHarmonicSelectionModeByChart: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  sideBandsSelectionModeByChart: Record<string, boolean>;
  setSideBandsSelectionModeByChart: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  customLineModeByChart: Record<string, string | null>;
  setCustomLineModeByChart: React.Dispatch<React.SetStateAction<Record<string, string | null>>>;
}

/**
 * Manages synced tool selection state (markers, harmonics, side bands)
 * Supports both internal state and external state (for performance optimization)
 */
export function useToolsSyncState({
  markerSelectionModeByChart: externalMarkerSelectionModeByChart,
  setMarkerSelectionModeByChart: externalSetMarkerSelectionModeByChart,
  harmonicSelectionModeByChart: externalHarmonicSelectionModeByChart,
  setHarmonicSelectionModeByChart: externalSetHarmonicSelectionModeByChart,
  sideBandsSelectionModeByChart: externalSideBandsSelectionModeByChart,
  setSideBandsSelectionModeByChart: externalSetSideBandsSelectionModeByChart,
  customLineModeByChart: externalCustomLineModeByChart,
  setCustomLineModeByChart: externalSetCustomLineModeByChart,
}: UseToolsSyncStateProps = {}): UseToolsSyncStateReturn {
  // Internal state (fallback when external props not provided)
  const [internalMarkerSelectionModeByChart, setInternalMarkerSelectionModeByChart] = useState<
    Record<string, boolean>
  >({});
  const [internalHarmonicSelectionModeByChart, setInternalHarmonicSelectionModeByChart] = useState<
    Record<string, boolean>
  >({});
  const [internalSideBandsSelectionModeByChart, setInternalSideBandsSelectionModeByChart] = useState<
    Record<string, boolean>
  >({});
  const [internalCustomLineModeByChart, setInternalCustomLineModeByChart] = useState<
    Record<string, string | null>
  >({});

  // Use external state when provided, otherwise use internal state
  const markerSelectionModeByChart = externalMarkerSelectionModeByChart ?? internalMarkerSelectionModeByChart;
  const setMarkerSelectionModeByChart =
    externalSetMarkerSelectionModeByChart ?? setInternalMarkerSelectionModeByChart;
  const harmonicSelectionModeByChart = externalHarmonicSelectionModeByChart ?? internalHarmonicSelectionModeByChart;
  const setHarmonicSelectionModeByChart =
    externalSetHarmonicSelectionModeByChart ?? setInternalHarmonicSelectionModeByChart;
  const sideBandsSelectionModeByChart = externalSideBandsSelectionModeByChart ?? internalSideBandsSelectionModeByChart;
  const setSideBandsSelectionModeByChart =
    externalSetSideBandsSelectionModeByChart ?? setInternalSideBandsSelectionModeByChart;
  const customLineModeByChart = externalCustomLineModeByChart ?? internalCustomLineModeByChart;
  const setCustomLineModeByChart = externalSetCustomLineModeByChart ?? setInternalCustomLineModeByChart;

  return {
    markerSelectionModeByChart,
    setMarkerSelectionModeByChart,
    harmonicSelectionModeByChart,
    setHarmonicSelectionModeByChart,
    sideBandsSelectionModeByChart,
    setSideBandsSelectionModeByChart,
    customLineModeByChart,
    setCustomLineModeByChart,
  };
}
